import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Check, Spinner } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { Button, Input } from '@/components/ui'
import { useForgotPasswordMutation } from '@/feature/auth/api'
import { LogoExtended } from '@/routes/layouts/public-layout'

const validationSchema = z.object({
  email: z.string().email()
})

type FormValues = z.infer<typeof validationSchema>

export const SendResetMail = () => {
  const { t } = useTranslation(`wizard`)
  const forgotPasswordMutation = useForgotPasswordMutation()

  const { register, formState, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      email: ``
    }
  })

  const onSubmit = handleSubmit(data => {
    forgotPasswordMutation.mutate(data.email)
  })

  const isLoginButtonDisabled = forgotPasswordMutation.isPending || !formState.isValid

  const buttonContent = useMemo(() => {
    if (forgotPasswordMutation.isPending) {
      return <Spinner className='animate-spin' />
    }
    if (forgotPasswordMutation.isSuccess) {
      return <Check size={20} />
    }
    return <span>{t(`login.screens.forgot_password.from.button`)}</span>
  }, [forgotPasswordMutation.isPending, forgotPasswordMutation.isSuccess, t])

  return (
    <div className='flex h-full w-full flex-col items-center justify-between bg-white p-8'>
      <LogoExtended />
      <div className='w-full max-w-md'>
        <h1 className='text-center text-4xl font-medium'>{t(`login.screens.forgot_password.title`)}</h1>
        <p className='mx-auto max-w-sm text-center text-lg text-secondary'>{t(`login.screens.forgot_password.description`)}</p>
        <div id='kc-form'>
          <div id='kc-form-wrapper'>
            <form onSubmit={onSubmit} className='mt-10 flex w-full flex-col gap-4'>
              <div className='w-full'>
                <Input {...register(`email`)} type='text' autoComplete='email' />

                {forgotPasswordMutation.isSuccess ? (
                  <span className='mt-1 text-sm text-green-300'>{t(`login.screens.forgot_password.success.title`)}</span>
                ) : null}
              </div>

              <Button name='login' type='submit' disabled={isLoginButtonDisabled}>
                {buttonContent}
              </Button>
            </form>
          </div>
        </div>
      </div>
      <div className='flex items-center justify-center gap-1'>
        <p className='text-muted-foreground'>{t(`sign_up.screens.welcome.login.text`)}</p>
        <Button asChild variant='link' size='sm'>
          <Link to='/auth/login'>{t(`sign_up.screens.welcome.login.button`)}</Link>
        </Button>
      </div>
    </div>
  )
}
